import { drawToCanvas, letterbox, sourceRegion, toTensorData, unpadBox } from "./preprocess.js";

const MAX_BOXES = 5;
const MAX_THRESHOLD = 0.4;

export const KNOWN_NORMALIZATION = Object.freeze({
  ssdlite320_mobilenet_v3_large: { mean: [0.5, 0.5, 0.5], std: [0.5, 0.5, 0.5] }
});

export const ALL_VIEWS = Object.freeze(["whole", "center"]);

export function sizeOf(source) {
  return {
    width: source.videoWidth || source.naturalWidth || source.width,
    height: source.videoHeight || source.naturalHeight || source.height
  };
}

export function wholeView(source, size, canvas) {
  const { pixels, pad } = letterbox(source, size, canvas);
  return { pixels, map: (box) => unpadBox(box, pad) };
}

export function centerView(source, size, canvas) {
  const { width, height } = sizeOf(source);
  const region = sourceRegion(width, height, "center");
  const pixels = drawToCanvas(source, size, canvas, "center");
  const clamp = (value) => Math.min(1, Math.max(0, value));
  return {
    pixels,
    map: (box) => ({
      ...box,
      x1: clamp((region.sx + box.x1 * region.sw) / width),
      y1: clamp((region.sy + box.y1 * region.sh) / height),
      x2: clamp((region.sx + box.x2 * region.sw) / width),
      y2: clamp((region.sy + box.y2 * region.sh) / height)
    })
  };
}

const VIEWS = { whole: wholeView, center: centerView };

export function intersectionOverUnion(first, second) {
  const left = Math.max(first.x1, second.x1);
  const top = Math.max(first.y1, second.y1);
  const right = Math.min(first.x2, second.x2);
  const bottom = Math.min(first.y2, second.y2);
  const overlap = Math.max(0, right - left) * Math.max(0, bottom - top);
  const union = areaOf(first) + areaOf(second) - overlap;
  return union > 0 ? overlap / union : 0;
}

export const areaOf = (box) => Math.max(0, box.x2 - box.x1) * Math.max(0, box.y2 - box.y1);

export function suppress(boxes, iouThreshold, limit = MAX_BOXES) {
  const kept = [];
  for (const box of [...boxes].sort((a, b) => b.score - a.score)) {
    if (kept.some((chosen) => intersectionOverUnion(chosen, box) > iouThreshold)) continue;
    kept.push(box);
    if (kept.length >= limit) break;
  }
  return kept;
}

export const bestScore = (scores) => {
  let best = 0;
  for (let index = 0; index < scores.length; index += 1) if (scores[index] > best) best = scores[index];
  return best;
};

export function decodeBoxes(boxes, scores, { scoreThreshold = 0.45, iouThreshold = 0.45, minArea = 0.015 } = {}) {
  const candidates = [];
  for (let index = 0; index < scores.length; index += 1) {
    if (scores[index] < scoreThreshold) continue;
    const offset = index * 4;
    const box = { x1: boxes[offset], y1: boxes[offset + 1], x2: boxes[offset + 2], y2: boxes[offset + 3], score: scores[index] };
    if (areaOf(box) < minArea) continue;
    candidates.push(box);
  }
  return suppress(candidates, iouThreshold);
}

export function coverMapping(sourceWidth, sourceHeight, viewWidth, viewHeight) {
  const scale = Math.max(viewWidth / sourceWidth, viewHeight / sourceHeight);
  return {
    scale,
    offsetX: (viewWidth - sourceWidth * scale) / 2,
    offsetY: (viewHeight - sourceHeight * scale) / 2
  };
}

export function projectBox(box, region, mapping) {
  const left = region.sx + box.x1 * region.sw;
  const top = region.sy + box.y1 * region.sh;
  return {
    left: mapping.offsetX + left * mapping.scale,
    top: mapping.offsetY + top * mapping.scale,
    width: (box.x2 - box.x1) * region.sw * mapping.scale,
    height: (box.y2 - box.y1) * region.sh * mapping.scale,
    score: box.score
  };
}

export class DogDetector {
  constructor({ loader, ortProvider }) {
    this.loader = loader;
    this.ortProvider = ortProvider;
    this.spec = null;
    this.session = null;
    this.canvas = null;
    this.ort = null;
    this.loading = null;
  }

  get configured() {
    return Boolean(this.spec?.variants);
  }

  get ready() {
    return Boolean(this.session);
  }

  async init(metadata) {
    this.spec = metadata?.detector || null;
    if (!this.configured) return false;
    this.loading ||= this.#load(metadata);
    return this.loading;
  }

  async #load(metadata) {
    const bytes = await this.loader.loadModelBytes({
      model_version: `${metadata.model_version}-detector`,
      variants: this.spec.variants
    });
    this.ort = await this.ortProvider();
    this.session = await this.ort.InferenceSession.create(bytes, { executionProviders: ["wasm"], graphOptimizationLevel: "all" });
    return true;
  }

  normalization() {
    const known = KNOWN_NORMALIZATION[this.spec?.architecture];
    if (known) return known;
    const { mean, std } = this.spec?.input || {};
    return { mean: mean || [0.5, 0.5, 0.5], std: std || [0.5, 0.5, 0.5] };
  }

  thresholds() {
    return {
      scoreThreshold: Math.min(this.spec?.score_threshold ?? 0.45, MAX_THRESHOLD),
      iouThreshold: this.spec?.iou_threshold ?? 0.45,
      minArea: this.spec?.min_box_area ?? 0.015,
      doubtRatio: this.spec?.doubt_ratio ?? 0.5
    };
  }

  outputs(output) {
    const names = this.session.outputNames;
    const boxes = output.boxes || output[names[0]];
    const scores = output.scores || output[names[1]];
    return boxes.dims?.length === 1 ? [scores, boxes] : [boxes, scores];
  }

  async view(name, source) {
    const { size } = this.spec.input;
    const { mean, std } = this.normalization();
    this.canvas ||= document.createElement("canvas");
    const build = VIEWS[name] || wholeView;
    const { pixels, map } = build(source, size, this.canvas);
    const tensor = new this.ort.Tensor("float32", toTensorData(pixels, mean, std), [1, 3, size, size]);
    const output = await this.session.run({ [this.session.inputNames[0]]: tensor });
    const [boxes, scores] = this.outputs(output);
    const limits = this.thresholds();
    return {
      dogs: decodeBoxes(boxes.data, scores.data, limits).map(map),
      best: bestScore(scores.data)
    };
  }

  async detect(source, views = ALL_VIEWS) {
    if (!this.session) throw new Error("Rilevatore non inizializzato");
    const limits = this.thresholds();
    const { width, height } = sizeOf(source);
    const found = [];
    let best = 0;
    for (const name of views.length ? views : ALL_VIEWS) {
      const result = await this.view(name, source);
      found.push(...result.dogs);
      best = Math.max(best, result.best);
      if (result.dogs.length) break;
    }
    return {
      dogs: suppress(found, limits.iouThreshold),
      best,
      empty: best < limits.scoreThreshold * limits.doubtRatio,
      region: { sx: 0, sy: 0, sw: width, sh: height },
      source: { width, height }
    };
  }

  async find(source, onError = () => {}, views = ALL_VIEWS) {
    if (!this.ready) return null;
    try {
      return await this.detect(source, views);
    } catch (error) {
      onError(error);
      return null;
    }
  }
}
